import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetIdentity, useList, useTranslate } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Flex, Spin, Typography, UploadFile } from "antd";
import styled from "styled-components";
import { Divider } from "@libertify/ui";
import type { AppUser } from "types";
import { MediaProjectListItem, PlaybookResponse } from "pages/media/types";
import axiosInstance from "../../services/api";
import { API_URL } from "@/constants";
import { useCreateProjectWithDocuments } from "../media/projects/hooks/useCreateProjectWithDocuments";
import { useProjectDetails } from "../media/projects/hooks/useProjectDetails";
import {
  isProjectLive,
  isProjectReady,
} from "../media/projects/helpers/helper";
import {
  dedupeAppend,
  removeByKey,
  toUploadFile,
  uploadFileKey,
} from "../create-experience/helpers/fileList";
import { useCreatorProfile } from "../creator-profile/hooks/useCreatorProfile";
import { useProjectPublications } from "../media/projects/hooks/useProjectPublications";
import TeacherOnboardingPage from "../teacher-onboarding";
import { ExistingProjectSelect } from "./components/ExistingProjectSelect";
import {
  DeepLinkUploader,
  MAX_DOCUMENTS,
} from "./components/DeepLinkUploader";

type Phase = "select" | "processing";

const LtiDeepLinkPage = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = searchParams.get("lti_deeplink") ?? "";
  const existingProjectId = searchParams.get("project_id");
  const ltiOrganizationId = searchParams.get("organization_id") ?? undefined;
  const ltiWorkspaceId = searchParams.get("workspace_id") ?? undefined;

  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("select");
  const [uploadProjectId, setUploadProjectId] = useState<string | null>(null);
  const [isAttaching, setIsAttaching] = useState(false);

  // A teacher with no Creator Profile sees the questionnaire first; once a
  // profile exists (or the teacher finishes the questionnaire here) we show the
  // uploader.
  const { data: identity } = useGetIdentity<AppUser>();
  const {
    profile,
    isLoading: isProfileLoading,
    isError: isProfileError,
    refetch: refetchProfile,
  } = useCreatorProfile({
    userId: identity?.id,
  });
  const hasCompletedOnboarding = !!profile;
  // Only decide once we have a definitive answer: a resolved profile
  // (completed) or a 404 (none — the hook maps that to profile: null, no
  // error). An error means "unknown", so never latch onboarding on it — that
  // would re-show the questionnaire and risk a duplicate profile. It gets the
  // retry screen below instead of an endless spinner.
  const isCheckingOnboarding =
    !identity?.id || isProfileLoading || isProfileError;

  // The session queries use staleTime: Infinity and the app disables
  // refetchOnWindowFocus, so an errored session never recovers on its own —
  // revive it explicitly rather than making the teacher reload.
  const queryClient = useQueryClient();
  const handleRetry = () => {
    void queryClient.invalidateQueries({ queryKey: ["session"] });
    void refetchProfile();
  };
  const [onboardingDone, setOnboardingDone] = useState(false);
  // to show the reveal screen when the teacher finishes the questionnaire
  const [initialHasCompleted, setInitialHasCompleted] = useState<
    boolean | null
  >(null);

  if (!isCheckingOnboarding && initialHasCompleted === null) {
    setInitialHasCompleted(hasCompletedOnboarding);
  }
  const needsOnboarding = initialHasCompleted === false && !onboardingDone;

  useEffect(() => {
    if (existingProjectId) {
      navigate(`/media/projects/${existingProjectId}/experience-v2`, {
        replace: true,
      });
    }
  }, [existingProjectId, navigate]);

  const [docName, setDocName] = useState("");
  const hasConfirmedRef = useRef(false);

  // Documents staged for upload. Nothing is sent until the teacher hits
  // "Create" — a multi-file flow can't upload on drop the way the old
  // single-file one did, or the second file would never make it.
  const [files, setFiles] = useState<UploadFile[]>([]);
  const { createProjectWithDocuments } = useCreateProjectWithDocuments(files);

  // On-demand only (enabled: false) — mirrors useOpenPublication's pattern —
  // so this never fires on mount, before LTI auth is settled. It's manually
  // refetched inside handleCreate, at the point auth is already known-good.
  const { refetch: fetchPlaybooks } = useList<PlaybookResponse>({
    resource: `media/${ltiOrganizationId}/playbooks`,
    filters: [{ field: "include_public", operator: "eq", value: true }],
    pagination: { current: 1, pageSize: 50, mode: "server" },
    queryOptions: {
      enabled: false,
    },
  });

  // Projects the teacher can attach directly instead of uploading a new
  // document. Always scoped to the LTI workspace — enabled requires
  // ltiWorkspaceId, so this never runs without it.
  const { data: existingProjectsData } = useList<MediaProjectListItem>({
    resource: `media/${ltiOrganizationId}/projects?workspace_id=${ltiWorkspaceId}`,
    pagination: { current: 1, pageSize: 100, mode: "server" },
    sorters: [{ field: "created", order: "desc" }],
    queryOptions: {
      enabled: Boolean(ltiOrganizationId) && Boolean(ltiWorkspaceId),
    },
  });
  const existingProjects = (existingProjectsData?.data ?? []).filter(
    (p) => isProjectReady(p.status) || isProjectLive(p.status)
  );

  const { data: projectData } = useProjectDetails({
    projectId: uploadProjectId ?? undefined,
  });

  const projectStatus = projectData?.data.status;
  const isProjectFailed = [
    "StoryboardFailed",
    "SlideGenerationFailed",
    "MediaFailed",
    "DocumentError",
  ].includes(projectStatus ?? "");

  const effectivePhase: Phase = isProjectFailed ? "select" : phase;
  const effectiveError = isProjectFailed
    ? t("lti.deepLink.uploadError")
    : error;

  const { isInstanceLive } = useProjectPublications(
    uploadProjectId ?? undefined
  );
  const publishedMedia = projectData?.data.media.find((m) =>
    isInstanceLive(m.instance_id)
  );

  // publication_mode is set to LtiProtected at project creation (see handleCreate) —
  // the backend auto-publishes once generation finishes, so it can't be changed
  // after the fact. Once that publish happens, just navigate to the project.
  useEffect(() => {
    if (
      !publishedMedia?.instance_id ||
      !uploadProjectId ||
      hasConfirmedRef.current
    )
      return;
    hasConfirmedRef.current = true;
    navigate(`/media/projects/${uploadProjectId}/experience-v2`, {
      replace: true,
    });
  }, [publishedMedia?.instance_id, uploadProjectId, navigate]);

  // Submits the LTI deep-link form back to Moodle for the given project. The
  // JWT is short-lived (~10 min) and video generation takes much longer, so
  // this fires as soon as a project_id exists — Moodle only needs that id in
  // the signed payload, not a finished video.
  const submitDeepLink = async (projectId: string) => {
    if (!session) return;
    const response = await axiosInstance.get<string>(
      `${API_URL}/lti/deep-link/complete`,
      {
        params: { lti_deeplink: session, project_id: projectId },
        responseType: "text",
      }
    );
    const range = document.createRange();
    range.setStart(document.body, 0);
    document.body.appendChild(range.createContextualFragment(response.data));
  };

  // antd calls beforeUpload once per file, so the functional update appends
  // each one of a multi-file drop in turn. It's also the only place with an
  // up-to-date count mid-batch, hence the MAX_DOCUMENTS clamp here: a single
  // drop of 50 files still lands as 35.
  const handleStageFile = (file: File) => {
    setFiles((curr) =>
      curr.length >= MAX_DOCUMENTS
        ? curr
        : dedupeAppend(
            curr,
            [toUploadFile(file, curr.length, "lti")],
            uploadFileKey
          )
    );
  };

  const handleRemoveFile = (file: UploadFile) => {
    setFiles((curr) => removeByKey(curr, uploadFileKey(file), uploadFileKey));
  };

  const handleCreate = async () => {
    if (files.length === 0) return;
    setDocName(
      files.length === 1
        ? (files[0]?.name ?? "")
        : t("lti.deepLink.documentsCount", { count: files.length })
    );
    setIsAttaching(false);
    setPhase("processing");
    setError(null);
    try {
      const { data: playbooksData } = await fetchPlaybooks();
      const defaultPlaybook = playbooksData?.data.find((p) => p.public);

      // Creates the project, uploads every staged document, and registers the
      // aggregated all_documents.pdf when there's more than one — the backend
      // merges them from that.
      const project = await createProjectWithDocuments({
        projectType: "flash_education",
        extraMediaSettings: { publication_mode: "LtiProtected" },
        organizationId: ltiOrganizationId,
        playbookId: defaultPlaybook?.id,
      });
      setUploadProjectId(project.id);

      await submitDeepLink(project.id);
    } catch {
      setPhase("select");
      setError(t("lti.deepLink.uploadError"));
    }
  };

  // Attaches an existing (already Ready/Live) project to this Moodle
  // activity instead of creating a new one from an uploaded document.
  const handleAttachExisting = async (project: MediaProjectListItem) => {
    setDocName(project.title);
    setIsAttaching(true);
    setPhase("processing");
    setError(null);
    try {
      await submitDeepLink(project.id);
    } catch {
      setIsAttaching(false);
      setPhase("select");
      setError(t("lti.deepLink.attachError"));
    }
  };

  if (!existingProjectId && isProfileError) {
    return (
      <StyledWrapper>
        <Alert
          type="error"
          message={t("lti.deepLink.error")}
          action={
            <Button size="small" onClick={handleRetry}>
              {t("buttons.refresh")}
            </Button>
          }
        />
      </StyledWrapper>
    );
  }

  if (existingProjectId || initialHasCompleted === null) {
    return (
      <Spin
        size="large"
        style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}
      />
    );
  }

  if (needsOnboarding) {
    return <TeacherOnboardingPage onComplete={() => setOnboardingDone(true)} />;
  }

  if (effectivePhase === "processing") {
    return (
      <StyledWrapper>
        <Flex vertical align="center" gap={12} style={{ padding: "40px 0" }}>
          <Spin size="large" />
          {docName ? <Typography.Text strong>{docName}</Typography.Text> : null}
          <Typography.Text type="secondary">
            {t(
              isAttaching ? "lti.deepLink.attaching" : "lti.deepLink.processing"
            )}
          </Typography.Text>
        </Flex>
      </StyledWrapper>
    );
  }

  return (
    <StyledWrapper>
      <DeepLinkUploader
        files={files}
        error={effectiveError}
        onStageFile={handleStageFile}
        onRemoveFile={handleRemoveFile}
        onCreate={() => {
          handleCreate().catch((error) =>
            console.error("Error creating experience", error)
          );
        }}
      >
        {existingProjects.length > 0 && (
          <>
            <Divider label={t("lti.deepLink.orExisting")} spacing="md" />
            <ExistingProjectSelect
              projects={existingProjects}
              onAttach={(project) => {
                handleAttachExisting(project).catch((error) =>
                  console.error("Error attaching existing project", error)
                );
              }}
            />
          </>
        )}
      </DeepLinkUploader>
    </StyledWrapper>
  );
};

export default LtiDeepLinkPage;

// ─── Styled components ───────────────────────────────────────────────────────

const StyledWrapper = styled.div`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  padding: 24px 16px;
  box-sizing: border-box;
`;
